// src/components/ui/Layout/AutoGrid.tsx
// Meso layout: Auto-fit grid following IonicLayout.md

import React, { forwardRef } from 'react';
import type { SpacingToken } from './Stack';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface AutoGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Minimum column width before wrapping (default: '280px') */
  minColumnWidth?: string;
  /** Gap between items */
  gap?: SpacingToken;
  /** Align items within their grid cell */
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  children: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const gapMap: Record<SpacingToken, string> = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const AutoGrid = forwardRef<HTMLDivElement, AutoGridProps>(
  (
    {
      minColumnWidth = '280px',
      gap = 'md',
      alignItems = 'stretch',
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const computedStyle: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`,
      gap: gapMap[gap],
      alignItems,
      width: '100%',
      ...style,
    };

    return (
      <div
        ref={ref}
        style={computedStyle}
        data-layout="AutoGrid"
        {...rest}
      >
        {children}
      </div>
    );
  }
);

AutoGrid.displayName = 'AutoGrid';

export default AutoGrid;
